import { useEffect, useState, useRef } from 'react';
import { Pencil } from 'lucide-react';

/* ─── API pública — usar en cualquier archivo ─────────────────────
   import { showPrompt } from '@/components/ui/GlobalPrompt';

   const dias = await showPrompt('¿Cuántos días extender el trial?', {
     titulo: 'Extender prueba',
     valorInicial: '7',
     tipo: 'number',          // 'text' (default) | 'number'
     confirmText: 'Extender',
     placeholder: 'Días',
   });
   if (dias === null) return;  // el usuario canceló
──────────────────────────────────────────────────────────────────── */
const EVENT = 'lexorbit:prompt';

export function showPrompt(message, opts = {}) {
  return new Promise((resolve) => {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { message, opts, resolve } }));
  });
}

export default function GlobalPrompt() {
  const [actual, setActual] = useState(null);
  const [valor, setValor] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      setActual(e.detail);
      setValor(e.detail.opts?.valorInicial ?? '');
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  useEffect(() => {
    if (actual) setTimeout(() => inputRef.current?.select(), 50);
  }, [actual]);

  if (!actual) return null;

  const { message, opts, resolve } = actual;
  const {
    titulo = 'Ingresar valor',
    confirmText = 'Aceptar',
    cancelText = 'Cancelar',
    tipo = 'text',
    placeholder = '',
  } = opts || {};

  const cerrar = (v) => { setActual(null); resolve(v); };
  const aceptar = () => {
    const v = valor.trim();
    cerrar(v === '' ? null : v);
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      style={{ fontFamily: 'Outfit, sans-serif' }}
      onClick={(e) => { if (e.target === e.currentTarget) cerrar(null); }}>
      <div className="w-full max-w-sm bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#C9A44C]/15 flex items-center justify-center flex-shrink-0">
            <Pencil className="w-5 h-5 text-[#C9A44C]" />
          </div>
          <h3 className="text-lg text-[#F8F6F0]" style={{ fontFamily: 'Playfair Display, serif' }}>{titulo}</h3>
        </div>
        <p className="text-[#B8B8B8] text-sm leading-relaxed mb-4">{message}</p>
        <input
          ref={inputRef}
          type={tipo}
          value={valor}
          placeholder={placeholder}
          onChange={(e) => setValor(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); aceptar(); }
            if (e.key === 'Escape') { e.preventDefault(); cerrar(null); }
          }}
          className="w-full bg-[#070707] border border-[rgba(201,164,76,0.25)] rounded-lg px-3 py-2.5 text-sm text-[#F8F6F0] outline-none focus:border-[#C9A44C]/60 transition-colors mb-5"
        />
        <div className="flex gap-3 justify-end">
          <button onClick={() => cerrar(null)}
            className="px-4 py-2.5 text-sm text-[#B8B8B8] hover:text-[#F8F6F0] transition-colors">
            {cancelText}
          </button>
          <button onClick={aceptar}
            className="px-4 py-2.5 text-sm rounded-lg border border-[#C9A44C]/30 bg-[#C9A44C]/20 text-[#C9A44C] hover:bg-[#C9A44C]/30 transition-colors">
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
